import { Tag, Plus, Minus } from "lucide-mui"
import React from "react"

import ActivityBase from "@/components/activities/base"
import OwnerLabel from "@/components/activities/owner_label"
import i18n from "@/i18n"
import type { ActivityVenueUpdatedTags } from "@/types/activities"

type UpdatedTagsProps = {
  activity: ActivityVenueUpdatedTags
  className?: string
}

const TagChangeGroup = ({ type, categories }: { type: "added" | "removed"; categories: Record<string, string[]> }) => {
  const Icon = type === "added" ? Plus : Minus
  const colorClasses = type === "added" ? "text-green-700 bg-green-50" : "text-red-700 bg-red-50"

  return (
    <>
      {Object.entries(categories).map(([category, values]) => {
        const categoryLabel = i18n.t(`app.venue_tags.categories.${category}`, { defaultValue: category })

        return (
          <div key={`${type}-${category}`} className="flex items-center gap-1">
            <Icon className={`w-3 h-3 ${type === "added" ? "text-green-500" : "text-red-500"}`} />
            <span className="text-gray-500">{categoryLabel}:</span>
            {values.map((value) => (
              <span key={value} className={`px-1.5 py-0.5 rounded text-xs ${colorClasses}`}>
                {i18n.t(`app.venue_tags.${category}.${value}`, { defaultValue: value })}
              </span>
            ))}
          </div>
        )
      })}
    </>
  )
}

const UpdatedTags: React.FC<UpdatedTagsProps> = ({ activity, className }) => {
  const changes = activity.parameters?.changes || {}

  return (
    <ActivityBase
      data-testid="panel-activity-venue-updated-tags"
      activityKey={activity.key}
      icon={Tag}
      createdAt={activity.createdAt}
      text={
        <>
          {i18n.react("app.activities.venue.updated_tags.description", {
            owner: <OwnerLabel owner={activity.owner} />,
          })}
          <div className="w-fit bg-gray-100 rounded-lg px-2 py-1 mt-2">
            <div className="flex flex-col gap-1 text-xs text-gray-700">
              {changes.added && Object.keys(changes.added).length > 0 ? (
                <TagChangeGroup type="added" categories={changes.added} />
              ) : null}
              {changes.removed && Object.keys(changes.removed).length > 0 ? (
                <TagChangeGroup type="removed" categories={changes.removed} />
              ) : null}
            </div>
          </div>
        </>
      }
      className={className}
    />
  )
}

export default UpdatedTags
